import { InputContainer } from '@creditornot/cb-form';
import { colors } from '@creditornot/cb-ingredients';
import { InputAlert, Input } from '@creditornot/cb-input';
import { Popover } from '@creditornot/cb-popover';
import { Select } from '@creditornot/cb-select';
import React, { useContext, useMemo } from 'react';
import { FieldInputProps } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import InputNumber from 'common/components/InputNumber';
import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import useMenuData from 'modules/menu/useMenuData';

import { getDiscountTypeOptions, getInvalidItemsForDiscount } from './utils';
import InputMonetary from '../../../../menu/items/components/ItemDetailsForm/InputMonetary';

type Props = {
  currency: Currency;
  discountInput: FieldInputProps<string | number | undefined, HTMLElement>;
  discountTypeInput: FieldInputProps<'absolute' | 'relative-percentage' | 'none', HTMLElement>;
  selectedItemIds: Set<string>;
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
`;

const DiscountInputsWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const DiscountTypeSelect = styled(Select)`
  max-width: 392px;
  margin-block-end: 12px;

  @media (min-width: ${440}px) {
    width: 256px;
    margin-inline-end: 16px;
    margin-block-end: 0;
  }
`;

const FixedPriceInput = styled(InputMonetary)`
  width: 120px;
`;

const PercentageInput = styled(InputNumber)`
  width: 120px;
`;

const DisabledEmptyInput = styled(Input).attrs({ disabled: true })`
  width: 120px;
`;

const InvalidDiscountAlert = styled(InputAlert).attrs({ size: 'small', variant: 'warning' })`
  margin-block-start: 12px;
  width: 100%;
`;

const InvalidItemList = styled(T.Small)`
  display: flex;
  flex-direction: column;
  width: 180px;
  max-height: 200px;
  overflow-y: auto;
`;

const ShowItemsButton = styled(T.Small)`
  cursor: pointer;
  color: ${colors.wolt100};
  font-weight: 500;
`;

const DiscountInputs = ({ currency, discountInput, discountTypeInput, selectedItemIds }: Props) => {
  const intl = useIntl();
  const { primaryLanguage } = useContext(MenuLocaleContext);
  const { items } = useMenuData();

  const discountTypeOptions = useMemo(() => getDiscountTypeOptions(intl), [intl]);

  const invalidItemsForDiscount = getInvalidItemsForDiscount(
    items,
    selectedItemIds,
    discountInput.value,
    discountTypeInput.value,
  );

  return (
    <Root>
      <InputContainer hint={intl.formatMessage({ id: 'menu.bulkActions.createWoltSpecials.discountHint' })}>
        <DiscountInputsWrapper>
          <DiscountTypeSelect
            {...discountTypeInput}
            clearable
            disableSearch
            onChange={option => {
              discountInput.onChange(undefined);
              discountTypeInput.onChange(option ?? 'none');
            }}
            options={discountTypeOptions}
          />
          {discountTypeInput.value === 'relative-percentage' ? (
            <PercentageInput {...discountInput} suffixText="%" />
          ) : discountTypeInput.value === 'absolute' ? (
            <FixedPriceInput {...discountInput} currency={currency} />
          ) : (
            <DisabledEmptyInput />
          )}
        </DiscountInputsWrapper>
      </InputContainer>
      {invalidItemsForDiscount.length > 0 && (
        <InvalidDiscountAlert>
          <FormattedMessage
            id="menu.bulkActions.createWoltSpecials.invalidItemsForDiscountMessage"
            values={{ count: invalidItemsForDiscount.length }}
          />{' '}
          <Popover
            placement="bottom"
            showOnClick={false}
            content={
              <InvalidItemList>
                {invalidItemsForDiscount.map(item => (
                  <div key={item.id}>{localize(item.name, primaryLanguage)}</div>
                ))}
              </InvalidItemList>
            }
            contentZIndex={100005}
          >
            <ShowItemsButton>
              <FormattedMessage id="menu.bulkActions.createWoltSpecials.showInvalidItems" />
            </ShowItemsButton>
          </Popover>
        </InvalidDiscountAlert>
      )}
    </Root>
  );
};

export default DiscountInputs;
